class CartDrawer extends HTMLElement {
    constructor() {
        super();
        this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
        this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
        this.setHeaderCartIconAccessibility();
    }

    setHeaderCartIconAccessibility() {
        const cartLink = document.querySelector('#cart-icon-bubble');
        if (!cartLink) return;
        cartLink.setAttribute('role', 'button');
        cartLink.setAttribute('aria-haspopup', 'dialog');
        cartLink.addEventListener('click', (event) => {
            event.preventDefault();
            this.open(cartLink);
        });
        cartLink.addEventListener('keydown', (event) => {
            if (event.code.toUpperCase() === 'SPACE') {
                event.preventDefault();
                this.open(cartLink);
            }
        });
    }

    open(triggeredBy) {
        if (triggeredBy) this.setActiveElement(triggeredBy);
        const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
        if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

        setTimeout(() => {
            this.classList.add('animate', 'active');
        });

        this.addEventListener('transitionend', () => {
            const containerToTrapFocusOn = this.classList.contains('is-empty') 
                ? this.querySelector('.drawer__inner-empty') 
                : document.getElementById('CartDrawer');
            const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
            trapFocus(containerToTrapFocusOn, focusElement);
        }, { once: true });

        document.body.classList.add('overflow-hidden');
    }

    close() {
        this.classList.remove('active');
        removeTrapFocus(this.activeElement);
        document.body.classList.remove('overflow-hidden');
    }

    setSummaryAccessibility(cartDrawerNote) {
        cartDrawerNote.setAttribute('role', 'button');
        cartDrawerNote.setAttribute('aria-expanded', 'false');
        if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
            cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
        }
        cartDrawerNote.addEventListener('click', (event) => {
            event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
        });
        cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
    }

    renderContents(parsedState) {
        this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
        this.productId = parsedState.id;
        this.getSectionsToRender().forEach((section) => {
            const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
            if (!sectionElement) return;
            sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        });

        setTimeout(() => {
            this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
            this.open();
        });
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }

    // ✅ IMPORTANT: On homepage, do NOT re-render cart-icon-bubble
    getSectionsToRender() {
        const sections = [
            { id: 'cart-drawer', selector: '#CartDrawer' },
        ];
        // Only add cart icon section if NOT homepage
        if (window.location.pathname !== '/') {
            sections.push({ id: 'cart-icon-bubble' });
        }
        return sections;
    }

    getSectionDOM(html, selector = '.shopify-section') {
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    }

    setActiveElement(element) {
        this.activeElement = element;
    }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
    // ✅ IMPORTANT: On homepage, do NOT re-render cart-icon-bubble
    getSectionsToRender() {
        const sections = [
            { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
        ];
        // Only add cart icon section if NOT homepage
        if (window.location.pathname !== '/') {
            sections.push({
                id: 'cart-icon-bubble',
                section: 'cart-icon-bubble',
                selector: '.shopify-section',
            });
        }
        return sections;
    }
}

customElements.define('cart-drawer-items', CartDrawerItems);

(function () {
    const path = window.location.pathname.replace(/\/+$/, '');
    const isHome = (path === '' || path === '/');
    const bubble = document.querySelector('#cart-icon-bubble');
    if (!bubble) return;

    const wrap = bubble.querySelector('.cart-count-bubble');
    const countEl = wrap ? wrap.querySelector('span[aria-hidden="true"]') : null;
    const srEl = wrap ? wrap.querySelector('.visually-hidden') : null;

    const original = {
        hadWrap: !!wrap,
        wrapDisplay: wrap ? wrap.style.display : '',
        wrapHiddenAttr: wrap ? wrap.getAttribute('hidden') : null,
        wrapHasHiddenClass: wrap ? wrap.classList.contains('hidden') : false,
        countText: countEl ? countEl.textContent : null,
        srText: srEl ? srEl.textContent : null,
    };

    function restoreOriginalThemeUI() {
        if (!wrap) return;
        if (countEl && original.countText != null) countEl.textContent = original.countText;
        if (srEl && original.srText != null) srEl.textContent = original.srText;
        wrap.style.display = original.wrapDisplay || '';
        if (original.wrapHiddenAttr === null) wrap.removeAttribute('hidden');
        else wrap.setAttribute('hidden', original.wrapHiddenAttr);
        if (original.wrapHasHiddenClass) wrap.classList.add('hidden');
        else wrap.classList.remove('hidden');
    }

    function setHomeCountUI(count) {
        let w = bubble.querySelector('.cart-count-bubble');
        // Empty cart: hide the bubble rather than showing a "0" badge (matches every other page).
        if (!count) {
            if (w) w.style.display = 'none';
            return;
        }
        if (!w) {
            w = document.createElement('div');
            w.className = 'cart-count-bubble';
            bubble.appendChild(w);
        }
        let c = w.querySelector('span[aria-hidden="true"]') || w.querySelector('span');
        if (!c) {
            c = document.createElement('span');
            c.setAttribute('aria-hidden', 'true');
            w.prepend(c);
        }
        let sr = w.querySelector('.visually-hidden');
        if (!sr) {
            sr = document.createElement('span');
            sr.className = 'visually-hidden';
            w.appendChild(sr);
        }
        c.textContent = `${count}`; // it is using for homepage
        sr.textContent = `${count} items`;
        w.classList.remove('hidden');
        w.removeAttribute('hidden');
        w.style.display = 'inline-block';
    }

    async function refreshCartCountHomeOnly() {
        if (!isHome) return;
        try {
            const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
                credentials: 'same-origin',
                headers: { Accept: 'application/json' },
            });
            const cart = await res.json();
            setHomeCountUI(cart.item_count || 0);
        } catch (e) { }
    }

    if (!isHome) {
        restoreOriginalThemeUI();
        return;
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', refreshCartCountHomeOnly);
    } else {
        refreshCartCountHomeOnly();
    }

    const originalFetch = window.fetch;
    window.fetch = function (...args) {
        return originalFetch.apply(this, args).then((response) => {
            try {
                const url = typeof args[0] === 'string' ? args[0] : args[0]?.url;
                if (url && url.includes('/cart/') && (url.includes('add') || url.includes('change') || url.includes('update') || url.includes('clear'))) {
                    setTimeout(refreshCartCountHomeOnly, 200);
                }
            } catch (e) { }
            return response;
        });
    };
})();
